import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Drawer,
    KeyValuePairs,
    TableProps,
    Popover,
    StatusIndicator,
    Link
} from '@cloudscape-design/components';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import TextFilter from '@cloudscape-design/components/text-filter';
import Pagination from '@cloudscape-design/components/pagination';
import { CustomAppLayout } from '../../layout';
import { useRouter } from '../../router';
import { useStore } from '../../store';
import Box from '@cloudscape-design/components/box';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import { useGlobalState } from '../../../context/GlobalStateContext';
import { formatPhone, storeJWT } from '../../../commons/functions';
import { useStoreAdmin } from '../../../context/useStoreAdmin';
import Icon from '@mdi/react';
import { mdiAccountOutline, mdiAllInclusiveBox, mdiCheckCircleOutline, mdiCloseCircleOutline, mdiContentCopy, mdiInfinity, mdiMapMarkerOutline, mdiShieldLock } from '@mdi/js';

const pageSize = 20;

export const ConfigUnitsPage = () => {
    const storeAdmin = useStoreAdmin();
    const { setLoading } = useGlobalState();
    const { idPropiedad, setIdPropiedad } = useGlobalState();
    const { idControl, setIdControl } = useGlobalState();
    const { JWT, setJWT } = useGlobalState();
    const [sortingColumn, setSortingColumn] = useState({
        sortingField: 'unidad',
        isDescending: false,
    });
    const [isDescending, setIsDescending] = useState<boolean>(false); // Remove this line


    const [selectedRow, setSelectedRow] = useState<any>(null);  // Stores selected row data
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [btnAuths, setBtnAuths] = useState(true);
    const [selectedAccesses, setSelectedAccesses] = useState<string[]>([]);

    const {
        actions: { makeHelpPanelHandler },
    } = useStore();
    const { navigate } = useRouter();
    const [selectedItems, setSelectedItems] = useState<UserUnit[]>([]);
    const [btnActionDisable, setBtnActionDisable] = useState<boolean>(true);
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [filteringText, setFilteringText] = useState('');
    const [units, setUnits] = useState<UserUnit[]>([]);

    type User = {
        idAutorizado: string;
        idControl: string;
        displayName: string;
        movil: string;
    };

    type UserUnit = {
        idUnidad: string;
        id: string;
        allowDistanceOpenInmueble: number;
        unidad: string;
        label: string;
        quotaMax: string;
        quotaActual: string;
        reqInfoUnidad: number;
        reqInfoTitle: string;
        canHandleGuests: number;
        isBlockUnit: string;
        pendingCount: string;
        users: number;
        alwaysVisible:boolean;
        policies?:any[]
    };

    const handleCheckboxChange = (unitId: string) => {
        setSelectedAccesses((prev) => {
            // If the access is already selected, remove it from the array, else add it
            if (prev.includes(unitId)) {
                return prev.filter(id => id !== unitId);
            } else {
                return [...prev, unitId];
            }
        });
    };
    const filteredUnits = units.filter(unit => {
        const searchWords = filteringText.toLowerCase().split(/\s+/).filter(Boolean);
        return searchWords.every(word =>
            unit.unidad.toLowerCase().includes(word) ||
            unit.idUnidad.toString().toLowerCase().includes(word)
        );
    });
    type SortableFields = keyof UserUnit;

    const field = sortingColumn.sortingField as SortableFields;

    const sortedUnits = [...filteredUnits].sort((a, b) => {
        const aValue = a[field];
        const bValue = b[field];

        const isNumeric = !isNaN(Number(aValue)) && !isNaN(Number(bValue));

        if (isNumeric) {
            const numA = Number(aValue);
            const numB = Number(bValue);
            return sortingColumn.isDescending ? numB - numA : numA - numB;
        } else {
            const strA = aValue?.toString().toLowerCase() ?? "";
            const strB = bValue?.toString().toLowerCase() ?? "";
            if (strA < strB) return sortingColumn.isDescending ? 1 : -1;
            if (strA > strB) return sortingColumn.isDescending ? -1 : 1;
            return 0;
        }
    });

    const paginatedUnits = sortedUnits.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );


    const handleSelectionChange = ({ detail }: any) => {
        setSelectedItems(detail.selectedItems);
        setBtnActionDisable(detail.selectedItems.length === 0);
        if (detail.selectedItems.length > 1) setDrawerOpen(false);
    };

    useEffect(() => {
        setBtnAuths(selectedAccesses.length == 0)
    }, [selectedAccesses]); // The effect runs every time selectedAccesses changes

    // Handle row click to fetch additional data and open the HelpPanel
    const handleRowClick = async (rowData: UserUnit) => {
        setSelectedRow(rowData);  // Store selected row data
        setLoading(true);
        try {
            // Simulate a fetch request to get additional data for the row
            // Example of a fetched data structure
            const unitsDetails = {
                idUnidad: rowData.idUnidad,
                unidad: rowData.unidad,
                quotaMax: rowData.quotaMax,
                quotaActual: rowData.quotaActual,
                reqInfoUnidad: rowData.reqInfoUnidad,
                canHandleGuests: rowData.canHandleGuests,
                isBlockUnit: rowData.isBlockUnit,
                pendingCount: rowData.pendingCount,
                users: rowData.users

            };
            setSelectedRow(unitsDetails);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
            setDrawerOpen(true);
        }
    };
    useEffect(() => {
    }, [selectedRow]);
    const handleRefresh = async () => {
        try {

            setLoading(true);
            const response = await fetch('https://api2.llavemovil.com/routing/v1/default.aspx', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-routing': '/admin/units/list',
                    'JWT': JWT,

                },
                body: JSON.stringify({
                    idPropiedad: idPropiedad,
                    units: false,
                    accesses: false,
                    policies: true, 
                    columns: "idUnidad, unidad, allowDistanceOpenInmueble, isBlockUnit, reqInfoUnidad, reqInfoTitle, canHandleGuests, (select count(distinct idControl) from Autorizados where idInmueble=Inmuebles.idInmueble) as users "
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }
            const data = await response.json();

            storeAdmin(data);
            // Create a new list with updated 'accesos' field before setting it to state
            const updateUnits = data.units.map((unit: UserUnit) => ({
                ...unit,
                userCount: unit.users,
                id: unit.idUnidad,       // idUnidad → id
                label: unit.unidad,      // unidad → label
                alwaysVisible: true,      // unidad → label
            }));
            setUnits(updateUnits);

        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        handleRefresh();
    }, []);

    type SortingColumn = {
        sortingField: keyof UserUnit;  // This should map to the keys of UserUnit
        isDescending: boolean;
    };
    type SortingChangeHandler = TableProps<UserUnit>['onSortingChange'];

    const handleSortingChange: SortingChangeHandler = (event) => {
        const { sortingColumn: eventSortingColumn, isDescending: eventIsDescending } = event.detail;

        setSortingColumn((prevSortingColumn) => ({
            sortingField: eventSortingColumn?.sortingField || 'unidad',
            isDescending:
                prevSortingColumn.sortingField === eventSortingColumn?.sortingField
                    ? !prevSortingColumn.isDescending
                    : false, // Default to ascending when a new column is clicked
        }));
        // your logic
    };
    return (
        <CustomAppLayout
            toolsHide

            contentType="form"
            breadcrumbs={
                <BreadcrumbGroup
                    items={[
                        { text: 'Inicio', href: '#home' },
                        { text: 'Configuración', href: '#config/default' },
                        { text: 'Unidades', href: '#create-transcription-job' },
                    ]}
                    expandAriaLabel="Show path"
                    ariaLabel="Breadcrumbs"
                />
            }
            content={
                <>
                    <Table
                        stripedRows
                        sortingColumn={sortingColumn} // Pass sortingColumn directly
                        sortingDescending={sortingColumn.isDescending} // Pass isDescending directly from state
                        onSortingChange={handleSortingChange} // Pass onSortingChange directly
                        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
                            `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
                        }
                        onSelectionChange={handleSelectionChange}
                        selectedItems={selectedItems}
                        onRowClick={({ detail }) => handleRowClick(detail.item)}
                        ariaLabels={{
                            selectionGroupLabel: "Items selection",
                            allItemsSelectionLabel: () => "select all",
                            itemSelectionLabel: ({ selectedItems }, item) => item.idUnidad,
                        }}
                        columnDefinitions={[

                            {
                                id: "idUnidad",
                                header: "ID",
                                cell: (item) => item.idUnidad,
                                sortingField: "idUnidad",
                                isRowHeader: true,
                            },
                            {
                                id: "unidad",
                                header: "Unidad",
                                cell: (item) => (<Link href={`#config/unit.edit?id=${item.idUnidad}`}>{item.unidad}</Link>),
                                isRowHeader: true,
                                sortingField: "unidad",
                            },
                            {
                                id: "users",
                                header: <Icon path={mdiAccountOutline} size={1}></Icon>,
                                cell: (item) => item.users,
                                sortingField: "users",
                                sortingComparator: (a, b) => Number(a.users) - Number(b.users),
                            },
                            {
                                id: "canHandleGuests",
                                header: "Invitados",
                                cell: (item) =>
                                    item.canHandleGuests === 1 ? (
                                        <Icon path={mdiCheckCircleOutline} size={1} color={'blue'} />
                                    ) : (
                                        <Icon path={mdiCloseCircleOutline} size={1} color={'grey'} />
                                    ),
                                isRowHeader: true,
                            },
                            {
                                id: "reqInfoTitle",
                                header: "Info adicional",
                                cell: (item) =>
                                    item.reqInfoTitle !== "" ? (
                                        <><Icon path={mdiCheckCircleOutline} size={1} color={'blue'} />{' '}{item.reqInfoTitle}</>
                                    ) : (
                                        <Icon path={mdiCloseCircleOutline} size={1} color={'grey'} />
                                    ),
                                isRowHeader: true,
                            },
                            {
                                id: "policies",
                                header: <Icon path={mdiShieldLock} size={1}></Icon>,
                                cell: (item) => {

                                    if (item.policies && item.policies.length>0) {
                                        return (
                                            <Popover
                                                dismissButton={false}
                                                position="top"
                                                size="small"
                                                triggerType="custom"
                                                content={
                                                    <StatusIndicator type="success">
                                                {item.policies.length}{' '}política{item.policies.length===1?"":"s"}{' asignadas'}
                                                    </StatusIndicator>
                                                }
                                            >
                                                {item.policies.length}
                                            </Popover>);
                                    }
                                },
                                isRowHeader: true,
                            },
                            {
                                id: "allowDistanceOpenInmueble",
                                header: <Icon path={mdiMapMarkerOutline} size={1}></Icon>,
                                cell: (item) => {
                                    const value = item.allowDistanceOpenInmueble;

                                    if (value === -1) {
                                        return (
                                            <Popover
                                                dismissButton={false}
                                                position="top"
                                                size="small"
                                                triggerType="custom"
                                                content={
                                                    <StatusIndicator type="success">
                                                        Distancia ilimitada
                                                    </StatusIndicator>
                                                }
                                            >
                                                <Icon path={mdiAllInclusiveBox} size={1}/>
                                            </Popover>);
                                    } else if (value === 0) {
                                        return (
                                            <Popover
                                                dismissButton={false}
                                                position="top"
                                                size="small"
                                                triggerType="custom"
                                                content={
                                                    <StatusIndicator type="success">
                                                        Heredado desde acceso
                                                    </StatusIndicator>
                                                }
                                            >
                                                <Icon path={mdiContentCopy} size={1} />
                                            </Popover>);
                                    } else {
                                        return `${value}m`;
                                    }
                                },
                                isRowHeader: true,
                                //sortingField: "allowDistanceOpenInmueble", // (optional) if you want to allow sorting
                            },
                        ]}
                        items={paginatedUnits}
                        //selectionType="multi"
                        trackBy="idUnidad"
                        empty={
                            <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                                <SpaceBetween size="m">
                                    <b>No tiene unidades registradas</b>
                                    <Button href='#config/units.add'>Crear unidad</Button>
                                </SpaceBetween>
                            </Box>
                        }
                        filter={
                            <TextFilter
                                filteringText={filteringText}
                                onChange={({ detail }) => {
                                    setFilteringText(detail.filteringText);
                                    setCurrentPageIndex(1);
                                }}
                            />
                        }
                        pagination={
                            filteredUnits.length > pageSize && (
                                <Pagination
                                    currentPageIndex={currentPageIndex}
                                    pagesCount={Math.ceil(filteredUnits.length / pageSize)}
                                    onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                                />
                            )
                        }

                        header={
                            <Header
                                counter={
                                    selectedItems.length
                                        ? "(" + selectedItems.length + "/" + pageSize + ")"
                                        : ""
                                }
                                actions={
                                    <SpaceBetween direction="horizontal" size="xs">

                                        <CollectionPreferences
                                            onConfirm={({ detail }) => console.log(detail)}
                                            //preferences={preferences}
                                            title="Reordenamiento de unidades"
                                            confirmLabel="Guardar cambios"
                                            contentDisplayPreference={{
                                                options: units
                                            }}
                                        />
                                        <Button variant="primary" href='#config/units.add'>
                                            Agregar unidad
                                        </Button>
                                    </SpaceBetween>
                                }
                            >Configuración de unidades
                            </Header>
                        }
                    />

                </>
            }
        />
    );
};